import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import Table from '@/components/table/components/Table.tsx';
import { discountsApiQueryKeys } from '@/features/dashboard/discounts/api/discountsApi.queryKeys.ts';
import discountsApi from '@/features/dashboard/discounts/api/discountsApi.ts';
import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import useDeleteDiscount from '@/features/dashboard/discounts/hooks/useDeleteDiscount.ts';
import useActivateDiscount from '@/features/dashboard/discounts/hooks/useActivateDiscount.ts';
import useDeactivateDiscount from '@/features/dashboard/discounts/hooks/useDeactivateDiscount.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import EditDiscountModal from '@/features/dashboard/discounts/components/EditDiscountModal.tsx';
import CreateDiscountModal from '@/features/dashboard/discounts/components/CreateDiscountModal.tsx';
import { DiscountListDto } from '@/features/dashboard/discounts/types/DiscountListDto.ts';

const PromoCodesTable = () => {
  const [editDiscountId, setEditDiscountId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const deleteMutation = useDeleteDiscount();
  const activateMutation = useActivateDiscount();
  const deactivateMutation = useDeactivateDiscount();
  const { confirmDeleteWithCallback, confirmWithCallback } =
    confirmDialogUseHook();

  const rowContextMenuItems: RowContextMenuItemParams<DiscountListDto>[] = [
    {
      label: 'Edytuj',
      action: (row) => setEditDiscountId(row.id),
    },
    {
      label: 'Aktywuj',
      visible: (row) => !row.active,
      action: (row) =>
        confirmWithCallback({
          title: 'Aktywuj zniżkę',
          description: (
            <>
              Czy na pewno chcesz aktywować zniżkę <b>{row.name}</b>?
            </>
          ),
          onConfirm: () => activateMutation.mutate(row.id),
        }),
    },
    {
      label: 'Dezaktywuj',
      visible: (row) => row.active,
      action: (row) =>
        confirmWithCallback({
          title: 'Dezaktywuj zniżkę',
          description: (
            <>
              Czy na pewno chcesz dezaktywować zniżkę <b>{row.name}</b>?
            </>
          ),
          onConfirm: () => deactivateMutation.mutate(row.id),
        }),
    },
    {
      label: 'Usuń',
      variant: 'destructive',
      action: (row) =>
        confirmDeleteWithCallback({
          title: 'Usuń kod promocyjny',
          description: (
            <>
              Czy na pewno chcesz usunąć kod promocyjny <b>{row.name}</b>?
            </>
          ),
          onConfirm: () => deleteMutation.mutate(row.id),
        }),
    },
  ];

  return (
    <>
      <Table
        queryKey={discountsApiQueryKeys.all()}
        fetchData={discountsApi.getAll}
        fetchTableDef={discountsApi.getTableDefinition}
        rowContextMenuItems={rowContextMenuItems}
        title="Kody promocyjne"
        primaryAction={
          <ActionButton
            tone="accent"
            size="sm"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus />
            <span>Dodaj kod</span>
          </ActionButton>
        }
      />

      {editDiscountId && (
        <EditDiscountModal
          discountId={editDiscountId}
          onClose={() => setEditDiscountId(null)}
        />
      )}

      {isCreateModalOpen && (
        <CreateDiscountModal onClose={() => setIsCreateModalOpen(false)} />
      )}
    </>
  );
};

export default PromoCodesTable;
